import type { Breakpoint } from '@mui/material/styles';
import type { SimpleCompactContentProps } from '../simple';
import type { MainSectionProps, HeaderSectionProps, LayoutSectionProps } from '../core';

import { merge } from 'es-toolkit';
import { useState, useCallback } from 'react';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams, useLocation, useSearchParams } from 'react-router';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';

import { useExaminationDetails } from 'src/hooks/examination';

import { getAlvineLessonDetailUrl, getAlvineTasksOverviewUrl } from 'src/utils/lesson-links';

import { Logo } from 'src/components/logo';
import { ConfirmDialog } from 'src/components/custom-dialog/confirm-dialog';

import { useTranslate } from '../../locales';
import { SimpleCompactContent } from '../simple';
import { MainSection, HeaderSection, LayoutSection } from '../core';

type LayoutBaseProps = Pick<LayoutSectionProps, 'sx' | 'children' | 'cssVars'>;

export type ExaminationLayoutProps = LayoutBaseProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    main?: MainSectionProps;
    content?: SimpleCompactContentProps & { compact?: boolean };
  };
};

export function ExaminationLayout({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'md',
}: ExaminationLayoutProps) {
  const { t } = useTranslate('examination');
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showQuitConfirm, setShowQuitConfirm] = useState(false);

  // todo: this logic could be written better
  const isStatisticsPage = location.pathname.includes('statistics');
  const isUserCertificateOverviewPage = location.pathname.includes('user-certificate-overview');
  const isExaminationCompleted = searchParams.get('completed') === 'true';

  const isNonExaminationPage = isStatisticsPage || isUserCertificateOverviewPage;

  const { examination } = useExaminationDetails(
    !isNonExaminationPage && id ? Number(id) : undefined
  );

  const shouldSkipConfirmation = isNonExaminationPage || isExaminationCompleted;

  const handleQuitClick = useCallback(() => {
    if (shouldSkipConfirmation) {
      if (isNonExaminationPage) {
        window.location.href = getAlvineTasksOverviewUrl();
      } else {
        const targetLessonId = examination?.lesson?.id || (id ? Number(id) : null);
        window.location.href = getAlvineLessonDetailUrl(targetLessonId);
      }
    } else {
      setShowQuitConfirm(true);
    }
  }, [shouldSkipConfirmation, isNonExaminationPage, examination, id]);

  const handleQuitConfirm = useCallback(() => {
    if (isNonExaminationPage) {
      window.location.href = getAlvineTasksOverviewUrl();
    } else {
      const targetLessonId = examination?.lesson?.id || (id ? Number(id) : null);
      window.location.href = getAlvineLessonDetailUrl(targetLessonId);
    }
  }, [examination, id, isNonExaminationPage]);

  const handleQuitCancel = useCallback(() => {
    setShowQuitConfirm(false);
  }, []);

  const renderHeader = () => {
    const headerSlotProps: HeaderSectionProps['slotProps'] = { container: { maxWidth: false } };

    const headerSlots: HeaderSectionProps['slots'] = {
      topArea: (
        <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
          This is an info Alert.
        </Alert>
      ),
      leftArea: <Logo withLink={false} />,
      rightArea: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
          {/** @slot Close button */}
          <IconButton
            onClick={handleQuitClick}
            sx={{
              width: 40,
              height: 40,
              color: 'common.black',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={slotProps?.header?.sx}
      />
    );
  };

  const renderFooter = () => null;

  const renderMain = () => {
    const { compact, ...restContentProps } = slotProps?.content ?? {};

    return (
      <MainSection {...slotProps?.main}>
        {compact ? (
          <SimpleCompactContent layoutQuery={layoutQuery} {...restContentProps}>
            {children}
          </SimpleCompactContent>
        ) : (
          children
        )}
      </MainSection>
    );
  };

  return (
    <>
      <LayoutSection
        /** **************************************
         * @Header
         *************************************** */
        headerSection={renderHeader()}
        /** **************************************
         * @Footer
         *************************************** */
        footerSection={renderFooter()}
        /** **************************************
         * @Styles
         *************************************** */
        cssVars={{ '--layout-simple-content-compact-width': '448px', ...cssVars }}
        sx={sx}
      >
        {renderMain()}
      </LayoutSection>

      <ConfirmDialog
        open={showQuitConfirm}
        onClose={handleQuitCancel}
        title={t('control.actions.quit')}
        content={t('control.question.confirmCancel')}
        cancelText={t('control.question.cancel')}
        onConfirm={handleQuitConfirm}
        confirmText={t('control.actions.quit')}
      />
    </>
  );
}
